import * as $ from 'jquery';
import * as isMobile from 'ismobilejs';
import { dispatchCustomEvent } from './custom-event';

declare const window: any;

/**
 * Create a cookie
 *
 * @param name the name of the cookie
 * @param val the value of the cookie
 * @param expDays the expiry of the cookie
 * @param path the path the cookie belongs to (default current page)
 */
export function createCookie(name: string, val: string, expDays: number, path = '/'): void {
  let expires = '';
  let date = new Date();

  if (expDays) {
    date.setTime(date.getTime() + (expDays * 24 * 60 * 60 * 1000));
    expires = '; expires=' + date.toUTCString();
  }

  document.cookie = `${name}=${val}${expires}; path=${path}`;
}

/**
 * Create a cookie that expires right now
 *
 * @export
 * @param name the name or key of the cookie
 * @param val the value of the cookie
 */
export function createSessionCookie(name: string, val = '') {
  createCookie(name, val, 0);
}

/**
 * Get a cookie by name
 *
 * @export
 * @param name the name of the cookie
 * @returns {string} the cookie
 */
export function getCookie(name: string): string {
  return (document.cookie.match('(^|; )' + name + '=([^;]*)') || 0)[2];
}

/**
 * Add at full stop character in front of a string
 * @param str
 */
export function toCssClass(str: string) {
  return str.charAt(0) === '.' ? str : `.${str}`;
}

let lastId = 0;
/**
 * Generate an identifier
 * @param prefix
 */
export function uniqueId(prefix = '') {
  lastId++;
  return `${prefix}${lastId}`;
}

/**
 * Convert a NodeListOf<Element> to a component map
 * {
 *  "ZONE_A": {
 *      node: 'node',
 *      componentType: 'carousel'
 *  }
 * }
 * @param elements
 */
export function toZoneComponentMap(elements: NodeListOf<Element>) {
  // this can be done as a reduce
  let zoneToComponentMap = {};
  for (let i = 0; i < elements.length; i++) {
    let node = elements[i] as HTMLElement;
    let zoneId = node.dataset['zoneId'];
    let componentType = node.dataset['componentType'];
    let stringOptions = node.dataset['options'];
    let options = stringOptions ? JSON.parse(stringOptions) : undefined;

    if (zoneId && componentType) {
      zoneId = zoneId.toUpperCase();
      zoneToComponentMap[zoneId] = {
        node: node,
        componentType: componentType,
        options: options
      };
    }
  }
  return zoneToComponentMap;
}

/**
 * Create a script node and append it to the parent element
 * @param src the location of the js file
 */
export function createScriptNode(src: string) {
  let node = document.createElement('script');
  node.src = src;
  node.type = 'text/javascript';
  node.charset = 'UTF-8';
  node.crossOrigin = 'anonymous';
  return node;
}

/**
 * Search the package markup for scrm-script and return an array src attribute values
 * @param packageMarkup
 */
export function findScrmScriptUrls(packageMarkup: string): Array<string> {
  let re = /<script.*?src="(.*?)"/gmi, result, resultAry = [];
  while ((result = re.exec(packageMarkup)) !== null) {
    resultAry.push(result[1]);
  }
  return resultAry;
}

/**
 * Search the content items for scrm-nodes and then convert them to script nodes
 * @param packages an array of package responses
 */
export function getScriptNodesFromPackages(packages: Array<string>): Array<HTMLScriptElement> {
  return packages
    .map(markup => findScrmScriptUrls(markup))
    .reduce((prev, curr) => prev.concat(curr))
    .map(url => createScriptNode(url));
}

/**
 * Search the node DOM tree for script elements and remove them
 * @param node a reference to an element
 */
export function removeScriptNodes(node: JQuery) {
  $(node).find('script').remove();
}

/**
 * Detects the current device & returns a valid StarsCRM string representation
 * of the device to be passed along as a query parameter to the service.
 */
export function detectPlatform() {
  if (isMobile.apple.device) {
    return (isMobile.apple.tablet) ? 'iostablet' : 'iosphone';
  } else if (isMobile.any) {
    return (isMobile.tablet || isMobile.seven_inch) ? 'androidtablet' : 'androidphone';
  }
  return 'desktop';
}

/**
 * Get the intersection of two arrays
 * @param arr1
 * @param arr2
 */
export function getArrayIntersect(arr1: Array<any>, arr2: Array<any>) {
  let result = [], tempObj = {};

  arr2.forEach(elem => tempObj[elem] = true);

  arr1.forEach(item => {
    if (item in tempObj) {
      result.push(item);
    }
  });

  return result;
}

/**
 * Return the shallow intersection of two objects
 * @param o1
 * @param o2
 */
export function getObjectIntersect(o1: Object, o2: Object) {
  return Object.keys(o1).filter(key => key in o2);
}

export function arrayToObject(ary: Array<any>) {
  return ary.reduce((obj, item) => {
    obj[item] = item;
    return obj;
  }, {});
}

/**
 * Find click events in the node with data attribute 'data-scrm-click',
 * iterate though each node found and add 'touchstart' and 'click' event listeners.
 * Grab the content using regex in each data attribute found and handle all cases
 *
 * [Examples]
 * <a data-scrm-click="href('/link/to/somewhere')">...</a>
 * <a data-scrm-click="receiveMessage('open/casino/game')"><img src="..." /></a>
 *
 * @export
 *
 */
export function addScrmClickEventsToChildren(parentNode: JQuery) {
  const dataNodes = parentNode.find('[data-scrm-click]');
  Array.prototype.slice.call(dataNodes).forEach(node => $(node).on('touchstart click', touchStartClick));
}

/**
 * Check if the parmater is an array
 *
 * @export
 * @param {any} arg
 * @returns
 */
export function isArray(arg: any) {
  return Object.prototype.toString.call(arg) === '[object Array]';
}

/**
 * Convert object key/ value pairs to a query string
 * @param obj
 */
export function toQueryString(obj: any) {
  let keyValues = [];

  for (let key in obj) {
    if (obj.hasOwnProperty(key)) {
      let value = obj[key];
      if (value) {
        keyValues.push(`${key}=${value}`);
      }
    }
  }

  return keyValues.join('&');
}

/**
 * Send a message over the stars bridge
 * @param msg
 */
export function callHost(msg: string) {
  if (window.stars && window.stars.postMessage) {
    window.stars.postMessage(msg); // desktop
  } else if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.stars && window.webkit.messageHandlers.stars.postMessage) {
    window.webkit.messageHandlers.stars.postMessage(msg); // ios
  }
}

function touchStartClick(evt: Event) {
  dispatchCustomEvent(window, 'scrmtargetedclose');

  const regex = /'(.*?)'/g;
  const attr = $(this).attr('data-scrm-click');
  const link = regex.exec(attr)[1];

  // ..check for both touchstart and click events...
  if (evt.type === 'touchstart') { $(this).off('click'); }

  if (/href/i.test(attr)) { window.location.href = link; }
  if (/receiveMessage/i.test(attr)) {
    if (window.ps && window.ps.clb) {
      window.ps.clb.getAppRef().receiveMessage(link);
    }
  }
  if (/popup/i.test(attr)) {
    window.open(link);
  }

}

/**
 * Get all query parameters in the url
 * return object {"mykey": "myvalue"}
 *
 * @export
 * @returns an key/value pair object of query params
 */
export function getQueryParams(): any {
  let paramMap = {};
  let queryString = document.location.search;

  // Remove the ? at the beginning if it exists
  if (queryString.indexOf('?') === 0) {
    queryString = queryString.substring(1);
  }

  // Separate each key/ value pair
  const parts = queryString.split('&')
    .filter(part => part.length > 0 && part !== '=');

  parts.forEach(part => {
    let keyValuePair = part.split('=');
    let key = decodeURIComponent(keyValuePair[0].toLowerCase());
    let value = decodeURIComponent(keyValuePair[1] || '');
    paramMap[key] = value;
  });

  return paramMap;
}

export function isAuthenticated() {

  const SBTK = getCookie('SBTK');
  const SBSG = getCookie('SBSG');
  const WBID = getCookie('WBID');

  if (SBTK && SBSG && WBID) {
    return true;
  } else {
    const tokens = this.getTokenValues(getQueryParams());
    if (!tokens) {
      if (window.ps && window.ps.adapt && window.ps.adapt.getAppRef && window.ps.adapt.getAppRef().getTokens) {
        return this.getTokenValues(window.ps.adapt.getAppRef().getTokens()) !== undefined;
      }
    }
    return tokens !== undefined;
  }
}

export function getTokenValues(tokens: any) {
  const { u, token, signature } = tokens;
  if (u && token && signature) {
    return {
      'SBTK': token,
      'SBSG': signature,
      'WBID': u
    };
  }
}




// WEBPACK FOOTER //
// ./src/helpers/utils.ts